import React from 'react';
import { FieldProps, utils } from '@rjsf/core';

const { getWidget, getUiOptions, isSelect, optionsList, getDefaultRegistry, hasWidget } = utils;

/**
 * A drop-in replacement for @rjsf/core@3.2.1's built-in StringField.
 *
 * The upstream StringField sets `StringField.defaultProps` (see
 * node_modules/@rjsf/core/lib/components/fields/StringField.js), which React 18 warns will be
 * removed from function components in a future major release - logged on every render of every
 * string-typed schema field, for every form in the app. Upgrading @rjsf/core to remove this is
 * a large, separate migration (3 major versions behind); this instead ports the field's exact
 * selection logic (same widget-resolution behaviour, verified against the upstream source) but
 * with default parameters in place of `defaultProps`, so the deprecated API is never used at all.
 */
const StringField: React.FC<React.PropsWithChildren<FieldProps>> = ({
  schema,
  name,
  uiSchema = {},
  idSchema,
  formData,
  required,
  disabled = false,
  readonly = false,
  autofocus = false,
  onChange,
  onBlur,
  onFocus,
  registry = getDefaultRegistry(),
  rawErrors,
}: FieldProps) => {
  const { title, format } = schema;
  const { widgets, formContext } = registry;
  const enumOptions = isSelect(schema) && optionsList(schema);
  let defaultWidget = enumOptions ? 'select' : 'text';

  if (format && hasWidget(schema, format, widgets)) {
    defaultWidget = format;
  }

  const { widget = defaultWidget, placeholder = '', ...options } = getUiOptions(uiSchema) ?? {};
  const Widget = getWidget(schema, widget, widgets);

  return (
    <Widget
      options={{ ...options, enumOptions }}
      schema={schema}
      uiSchema={uiSchema}
      id={idSchema?.$id}
      label={title === undefined ? name : title}
      value={formData}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      required={required}
      disabled={disabled}
      readonly={readonly}
      formContext={formContext}
      autofocus={autofocus}
      registry={registry}
      placeholder={placeholder}
      rawErrors={rawErrors}
    />
  );
};
export default StringField;
